import { z } from "zod";
import { lineageHasSupersededInput } from "./epistemic-lineage.js";
import { ActorKindSchema, AttributionStatusSchema, ClaimOriginSchema, ClaimTypeSchema, ClassificationStateSchema, InferenceModeSchema, EvidenceAuthoritySchema, EvidenceRelationSchema, StanceSchema, VerificationSchema, assessClaim, claimCitationId, classifyLegacyFact, } from "./epistemic.js";
/**
 * Deterministic epistemic selection (ADR-0117). Runs BEFORE any prose model:
 * every record a Current Position / Ask prompt can see is first assigned one
 * class from the table below, and only the first four classes are eligible to
 * be stated as a present position. Prompt wording is a second line of
 * defence; `checkPositionSentence` / `checkAnswerText` are the third.
 */
export const EPISTEMIC_CLASSES = [
    "self-position",
    "agreed",
    "verified-observation",
    "counterparty-position",
    "recorded-decision",
    "source-reported",
    "derived-inference",
    "proposal",
    "assumption",
    "scenario",
    "open-question",
    "system-record",
    "recorded-unverified",
    "rejected-or-contradicted",
    "unclassified-legacy",
];
export const EpistemicClassSchema = z.enum(EPISTEMIC_CLASSES);
export const CURRENT_POSITION_ELIGIBLE_CLASSES = new Set([
    "self-position",
    "agreed",
    "verified-observation",
    "counterparty-position",
]);
export const EPISTEMIC_CLASS_HEADINGS = {
    "self-position": "ORGANISATION POSITION / DECISION (explicit organisation stance: adopted)",
    agreed: "AGREED BETWEEN PARTIES (organisation and counterparty stances both recorded)",
    "verified-observation": "VERIFIED OBSERVATIONS (supported by primary or controlled evidence)",
    "counterparty-position": "COUNTERPARTY POSITIONS / STATEMENTS (the named party's own record or stance)",
    "recorded-decision": "RECORDED DECISIONS (adopting party not recorded — not a confirmed position)",
    "source-reported": "SOURCE-REPORTED MATERIAL (what a source or draft says — not a position, not the named party's own statement)",
    "derived-inference": "DERIVED INFERENCES (synthesis, hypothesis or implication produced by reasoning — not evidence, no organisation stance; keep them visibly inferential)",
    proposal: "PROPOSALS (proposed, not adopted or agreed)",
    assumption: "ASSUMPTIONS (working assumptions, not established fact)",
    scenario: "SCENARIOS (conditional — every consequence holds only under the stated condition; never a forecast or position)",
    "open-question": "OPEN QUESTIONS (unresolved — do not treat as answered)",
    "system-record": "SYSTEM RECORDS (written by an integration; a record of system state only)",
    "recorded-unverified": "RECORDED CLAIMS (unverified, no adopting stance)",
    "rejected-or-contradicted": "REJECTED OR CONTRADICTED (not current)",
    "unclassified-legacy": "UNCLASSIFIED LEGACY RECORDS (stored with provenance; epistemic status not yet recorded — never a position)",
};
function factProposition(fact) {
    return `${fact.key.replaceAll("_", " ")} = ${fact.value}${fact.unit ? ` ${fact.unit}` : ""}`;
}
/**
 * Class of an explicit claim. Order matters: contradiction and rejection
 * beat everything; type-level uncertainty (question/assumption/scenario/
 * proposal) is never promoted by a stance; only an explicit organisation
 * `adopted` stance makes an organisation position.
 */
export function classifyExplicitClaim(claim, assessment) {
    const self = assessment.selfStance?.stance ?? null;
    if (assessment.verification === "contradicted" || self === "rejected") {
        return "rejected-or-contradicted";
    }
    if (claim.claimType === "question")
        return "open-question";
    if (claim.claimType === "assumption")
        return "assumption";
    // Scenario containment: a scenario — or anything reasoned from one — stays
    // conditional even if the organisation adopts it as a planning scenario.
    if (claim.claimType === "scenario" || assessment.conditional)
        return "scenario";
    if (assessment.agreed)
        return "agreed";
    // Only an explicit organisation `adopted` stance makes a position — a
    // proposal or derived inference the organisation adopts becomes its
    // position through that auditable act (lineage is kept).
    if (self === "adopted")
        return "self-position";
    // A derived claim without an adopting stance stays an inference, however
    // often it is retrieved or restated (no self-promotion by repetition).
    if (claim.claimType === "derived")
        return "derived-inference";
    if (claim.claimType === "proposal" ||
        self === "proposed" ||
        assessment.externalStances.some((row) => row.stance === "proposed")) {
        return "proposal";
    }
    if (claim.claimType === "observation" &&
        (assessment.verification === "verified" || assessment.verification === "corroborated")) {
        return "verified-observation";
    }
    if (assessment.attribution.status === "direct" &&
        assessment.attribution.actor?.kind === "external" &&
        (claim.claimType === "statement" ||
            claim.claimType === "position" ||
            claim.claimType === "decision")) {
        return "counterparty-position";
    }
    if (assessment.attribution.status === "reported")
        return "source-reported";
    if (assessment.strongestAuthority !== null && assessment.verification === "source-only") {
        return "source-reported";
    }
    return "recorded-unverified";
}
function conditionFromLineage(claim, resolve, depth = 0) {
    if (depth > 4)
        return null;
    for (const ref of claim.derivedFrom) {
        if (ref.kind !== "claim")
            continue;
        const input = resolve(ref.id);
        if (!input)
            continue;
        const found = input.condition ?? conditionFromLineage(input, resolve, depth + 1);
        if (found)
            return found;
    }
    return null;
}
function entryFromClaim(claim, resolve, fact) {
    const assessment = assessClaim(claim, resolve);
    const cls = classifyExplicitClaim(claim, assessment);
    const claimCite = claimCitationId(claim.id);
    return {
        class: cls,
        state: "explicit",
        citationIds: fact ? [fact.citationId, claimCite] : [claimCite],
        proposition: claim.proposition,
        claimType: claim.claimType,
        factKey: fact?.key ?? claim.factKey,
        source: fact?.source ?? claim.evidence[0]?.source ?? null,
        asOf: fact?.asOf ?? claim.evidence[0]?.asOf ?? null,
        note: claim.note,
        originLabel: fact?.originLabel ?? null,
        authority: assessment.strongestAuthority,
        attribution: {
            actorLabel: assessment.attribution.actor?.label ?? null,
            actorKind: assessment.attribution.actor?.kind ?? null,
            status: assessment.attribution.status,
        },
        verification: assessment.verification,
        selfStance: assessment.selfStance?.stance ?? null,
        externalStances: assessment.externalStances.map((row) => ({
            actorLabel: row.actor.label,
            stance: row.stance,
            evidenced: row.evidenced,
        })),
        evidence: claim.evidence.map((row) => ({
            relation: row.relation,
            authority: row.authority,
            source: row.source,
            asOf: row.asOf,
            sourceActorLabel: row.sourceActor?.label ?? null,
            sourceActorKind: row.sourceActor?.kind ?? null,
        })),
        derived: assessment.derived,
        origin: assessment.origin,
        inferenceMode: assessment.inferenceMode,
        condition: claim.condition ?? conditionFromLineage(claim, resolve),
        conditional: assessment.conditional,
        lineage: claim.derivedFrom,
        lineageSuperseded: lineageHasSupersededInput(claim, resolve),
        competing: fact?.competing ?? false,
        at: assessment.selfStance?.asOf ?? fact?.asOf ?? claim.evidence[0]?.asOf ?? claim.createdAt,
    };
}
function entryFromLegacyFact(fact) {
    const legacy = classifyLegacyFact(fact);
    const machine = legacy.state === "machine-classified" ? legacy : null;
    return {
        class: machine ? "system-record" : "unclassified-legacy",
        state: legacy.state,
        citationIds: [fact.citationId],
        proposition: factProposition(fact),
        claimType: machine?.claimType ?? null,
        factKey: fact.key,
        source: fact.source,
        asOf: fact.asOf,
        note: fact.note ?? null,
        originLabel: fact.originLabel ?? null,
        authority: machine?.authority ?? null,
        attribution: { actorLabel: null, actorKind: null, status: "none" },
        verification: machine ? "source-only" : null,
        selfStance: null,
        externalStances: [],
        evidence: [],
        derived: false,
        origin: null,
        inferenceMode: null,
        condition: null,
        conditional: false,
        lineage: [],
        competing: fact.competing ?? false,
        at: fact.asOf,
    };
}
function emptyEntry(partial) {
    return {
        factKey: null,
        source: null,
        asOf: null,
        note: null,
        originLabel: null,
        authority: null,
        attribution: { actorLabel: null, actorKind: null, status: "none" },
        verification: null,
        selfStance: null,
        externalStances: [],
        evidence: [],
        derived: false,
        origin: null,
        inferenceMode: null,
        condition: null,
        conditional: false,
        lineage: [],
        competing: false,
        at: null,
        ...partial,
    };
}
/**
 * Pure selection. Explicit claims bound to a current fact replace that fact's
 * legacy entry; claims bound to a fact that is no longer current are
 * historical and excluded (ordinary currency). Superseded claims are
 * excluded. Legacy material stays in the output as `unclassified-legacy`.
 */
export function selectEpistemicEntries(input) {
    const claims = (input.claims ?? []).filter((row) => row.supersededAt === null);
    const all = new Map();
    for (const row of [...(input.lineage ?? []), ...(input.claims ?? [])])
        all.set(row.id, row);
    const resolve = (id) => all.get(id) ?? null;
    const factsByCitation = new Map(input.facts.map((fact) => [fact.citationId, fact]));
    const claimedFacts = new Set();
    const entries = [];
    for (const claim of claims) {
        if (claim.factCitationId) {
            const fact = factsByCitation.get(claim.factCitationId);
            if (!fact)
                continue; // bound to a historical value — not current
            claimedFacts.add(fact.citationId);
            entries.push(entryFromClaim(claim, resolve, fact));
            continue;
        }
        entries.push(entryFromClaim(claim, resolve, null));
    }
    for (const fact of input.facts) {
        if (claimedFacts.has(fact.citationId))
            continue;
        entries.push(entryFromLegacyFact(fact));
    }
    for (const decision of input.decisions ?? []) {
        entries.push(emptyEntry({
            class: "recorded-decision",
            state: "machine-classified",
            citationIds: [decision.citationId],
            proposition: decision.text,
            claimType: "decision",
            asOf: decision.at.slice(0, 10),
            at: decision.at,
        }));
    }
    for (const question of input.openQuestions ?? []) {
        entries.push(emptyEntry({
            class: "open-question",
            state: "machine-classified",
            citationIds: [question.citationId],
            proposition: question.label,
            claimType: "question",
        }));
    }
    const byCitation = new Map();
    for (const entry of entries) {
        for (const id of entry.citationIds)
            byCitation.set(id, entry);
    }
    return {
        entries,
        eligible: entries.filter((entry) => CURRENT_POSITION_ELIGIBLE_CLASSES.has(entry.class)),
        byCitation,
    };
}
export function entriesOfClass(selection, cls) {
    return selection.entries.filter((entry) => entry.class === cls);
}
// ─── Rendering ──────────────────────────────────────────────────────────────
function clip(text, max) {
    const trimmed = text.trim();
    if (trimmed.length <= max)
        return trimmed;
    return `${trimmed.slice(0, Math.max(0, max - 1))}…`;
}
/** One prompt line carrying the full epistemic envelope for an entry. */
export function renderEpistemicEntryLine(entry) {
    const parts = [
        `[${entry.citationIds.join("] [")}]`,
        `class=${entry.class}`,
        `state=${entry.state}`,
        entry.claimType ? `type=${entry.claimType}` : null,
        entry.originLabel ? `recorded-on=${entry.originLabel}` : null,
        `claim="${clip(entry.proposition, 600)}"`,
        entry.attribution.actorLabel
            ? `attributed-to=${entry.attribution.actorLabel} (${entry.attribution.status}${entry.attribution.status === "reported"
                ? ": a source says this; the party has not said it on record"
                : ""})`
            : null,
        entry.verification ? `verification=${entry.verification}` : "verification=not-recorded",
        `organisation-stance=${entry.selfStance ?? "none-recorded"}`,
        entry.externalStances.length > 0
            ? `other-stances=${entry.externalStances
                .map((row) => `${row.actorLabel}:${row.stance}${row.evidenced ? "" : " (reported by the organisation; not the party's own record)"}`)
                .join(",")}`
            : null,
        entry.authority ? `strongest-authority=${entry.authority}` : null,
        entry.evidence.length > 0
            ? `evidence=${entry.evidence
                .slice(0, 5)
                .map((row) => `${row.relation}/${row.authority} "${clip(row.source, 120)}" asOf=${row.asOf}${row.sourceActorLabel ? ` by=${row.sourceActorLabel}` : ""}`)
                .join("; ")}`
            : null,
        entry.source ? `source="${clip(entry.source, 200)}"` : null,
        entry.asOf ? `asOf=${entry.asOf}` : null,
        entry.derived
            ? `derived=yes (${entry.inferenceMode ?? "inference"}; origin=${entry.origin ?? "unknown"}; lineage=${entry.lineage
                .map((ref) => `${ref.kind}:${ref.id}`)
                .join(",")}; never stronger than its inputs)`
            : null,
        entry.lineageSuperseded
            ? "LINEAGE-INPUT-SUPERSEDED (a record this was reasoned from has since been superseded; re-examine before relying on it)"
            : null,
        entry.condition
            ? `CONDITION="${clip(entry.condition, 300)}" (consequences hold only if this occurs)`
            : null,
        entry.competing ? "COMPETING same-period values (do not resolve)" : null,
        entry.note ? `note (explanatory only)="${clip(entry.note, 400)}"` : null,
    ];
    return parts.filter((part) => Boolean(part)).join(" | ");
}
/** Class-grouped prompt block; empty classes are omitted. */
export function renderEpistemicSections(selection, opts = {}) {
    const classes = opts.classes ?? EPISTEMIC_CLASSES;
    const blocks = [];
    for (const cls of classes) {
        const rows = selection.entries.filter((entry) => entry.class === cls);
        if (rows.length === 0)
            continue;
        blocks.push([`### ${EPISTEMIC_CLASS_HEADINGS[cls]}`, ...rows.map(renderEpistemicEntryLine)].join("\n"));
    }
    if (blocks.length === 0)
        return "";
    return [opts.heading ?? "## EPISTEMICALLY CLASSIFIED RECORDS", ...blocks].join("\n\n");
}
const AUTHORITY_SOURCE_LABEL = {
    primary: "The primary source",
    "controlled-current": "The controlled record",
    draft: "The draft",
    secondary: "A secondary source",
    conversational: "A conversational record",
    derived: "A derived record",
};
function valueSuffix(entry) {
    const bits = [
        entry.source ? `source: ${clip(entry.source, 120)}` : null,
        entry.asOf ? `as of ${entry.asOf}` : null,
    ].filter(Boolean);
    return bits.length > 0 ? ` (${bits.join("; ")})` : "";
}
function stripStop(text) {
    return clip(text, 400).replace(/[.\s]+$/, "");
}
/**
 * Deterministic sentence for a skeleton Current Position. The wording keeps
 * each class's boundary visible: reported material says what the source
 * states; legacy says it is an unclassified record.
 */
export function renderEntrySentence(entry) {
    const prop = stripStop(entry.proposition);
    const on = entry.originLabel ? `${entry.originLabel} — ` : "";
    switch (entry.class) {
        case "self-position":
            return `${on}Current organisation position (explicitly adopted${entry.at ? `, as of ${entry.at.slice(0, 10)}` : ""}${entry.derived ? "; originated as a derived inference" : ""}): ${prop}.`;
        case "agreed":
            return `${on}Agreed with ${entry.externalStances
                .filter((row) => row.stance === "agreed" && row.evidenced)
                .map((row) => row.actorLabel)
                .join(", ") || "the counterparty"}: ${prop}.`;
        case "verified-observation":
            return `${on}Verified (${entry.verification}): ${prop}${valueSuffix(entry)}.`;
        case "counterparty-position":
            return `${on}${entry.attribution.actorLabel ?? "The counterparty"} has stated on its own record: ${prop}${valueSuffix(entry)}.`;
        case "recorded-decision":
            return `${on}Latest recorded decision (${entry.asOf ?? "undated"}; adopting party not recorded): ${clip(entry.proposition, 220)}`;
        case "source-reported": {
            const label = entry.authority ? AUTHORITY_SOURCE_LABEL[entry.authority] : "A recorded source";
            return `${on}${label}${entry.source ? ` (${clip(entry.source, 120)})` : ""} states: “${prop}.”`;
        }
        case "derived-inference":
            if (entry.inferenceMode === "synthesis") {
                return `${on}Taken together, the records suggest (derived inference, not established): ${prop}.`;
            }
            if (entry.inferenceMode === "implication") {
                return `${on}If the records it follows from hold, it would follow that (implication, not established): ${prop}.`;
            }
            return `${on}One possibility (hypothesis, not established): ${prop}.`;
        case "proposal":
            return `${on}Proposed, not adopted or agreed: ${prop}.`;
        case "assumption":
            return `${on}Working assumption, not established fact: ${prop}.`;
        case "scenario":
            return entry.condition
                ? `${on}Scenario only — if ${stripStop(entry.condition).replace(/^if\s+/i, "")}: ${prop}.`
                : `${on}Scenario only, not a forecast or position: ${prop}.`;
        case "open-question":
            return `${on}Remains unresolved: ${prop}.`;
        case "system-record":
            return `${on}The system record (${entry.source ?? "integration"}) shows ${prop}${entry.asOf ? ` as of ${entry.asOf}` : ""}.`;
        case "recorded-unverified":
            return `${on}Recorded claim, unverified and not adopted: ${prop}${valueSuffix(entry)}.`;
        case "rejected-or-contradicted":
            return `${on}Rejected or contradicted, not current: ${prop}.`;
        case "unclassified-legacy":
            return `${on}Recorded ${prop}${valueSuffix(entry)} — unclassified record, not presented as a position.`;
    }
}
const NEGATED_SETTLEMENT = /\b(?:not|no|nothing|none|neither|yet to be|never|nor|without)\b(?:\s+\w+){0,3}?\s+(?:settled|agreed|finali[sz]ed|resolved|concluded|confirmed|signed off|agreement|consensus)\b/gi;
const SETTLEMENT = /\b(?:settled|agreed|agreement (?:has been |was |is )?reached|consensus|finali[sz]ed|signed off|concluded|resolved)\b/i;
const CONFIRMATION = /\bconfirm(?:ed|s)?\b/i;
const POSITION_LANGUAGE = /\b(?:current position|our position|(?:its|their|the organisation'?s|the company'?s) position|has (?:adopted|decided|committed)|have (?:adopted|decided|committed)|decided to|is committed to|are committed to)\b/i;
const ATTRIBUTION_VERBS = "(?:has |have |had )?(?:indicated|confirmed|agreed|stated|said|committed|decided|signalled|signaled|announced|requested|asked|expressed|accepted|approved|wants|wishes|intends|plans|expects|will)";
/**
 * Completed acts a party must have performed on record. Inside an explicitly
 * labelled inference (synthesis / hypothesis / scenario), forward-looking or
 * wish verbs ("whether MoIP will ask…") are legitimate speculation; these are
 * not — "MoIP has indicated…" is an assertion about the record, whatever the
 * label.
 */
const COMPLETED_ATTRIBUTION_VERBS = "(?:has |have |had )?(?:indicated|confirmed|agreed|stated|said|committed|decided|signalled|signaled|announced|requested|asked|expressed|accepted|approved)";
const GENERIC_DIRECT_ATTRIBUTION = new RegExp(`\\b[A-Z][\\w&.-]*(?:\\s+[A-Z][\\w&.-]*)*\\s+(?:has|have)\\s+(?:indicated|confirmed|agreed|stated|committed|decided|signalled|signaled|announced|accepted|approved)\\b`);
const REPORTING_FRAME = /\b(?:states?|stated|record(?:s|ed)?|reports?|reported|according to|says|draft|document|source|wording|text)\b/i;
const INFERENCE_FRAME = /\b(?:suggests?|suggested|may|might|could|possib(?:le|ly|ility)|hypothes[ie]s|synthes[ie]s|infer(?:red|ence)?|appears?|likely|taken together|points? (?:to|toward)|derived|implication|impl(?:y|ies|ied)|(?:it )?follows? from|it follows|would (?:mean|imply|follow|suggest))\b/i;
/**
 * A hypothesis is weaker than a synthesis: restating one needs a frame that
 * keeps it a possibility. "Synthesis: X is Y" or "It follows that X" would
 * quietly upgrade a hypothesis (anti-laundering, P2).
 */
const HYPOTHESIS_FRAME = /\b(?:may|might|could|possib(?:le|ly|ility)|hypothes[ie]s|whether|perhaps|unconfirmed|not (?:yet )?established|worth investigating|if)\b/i;
/**
 * Falsity language. "No evidence for X" must never become "X is false":
 * saying something is false, untrue or not the case needs a record that
 * contradicts it (gap detection vs contradiction, P2).
 */
const FALSITY = /\b(?:(?:is|are|was|were|has been|have been) (?:false|untrue|incorrect|wrong|disproven|refuted|baseless)|(?:is|are|was|were) not (?:true|the case|correct)|did not (?:happen|occur))\b/i;
const CONDITIONAL_FRAME = /\b(?:if|scenario|were to|in the event|should .{1,40} (?:occur|happen|fall|rise)|under (?:the|this|that) (?:scenario|assumption|condition))\b/i;
const STOP_WORDS = new Set([
    "that",
    "this",
    "with",
    "from",
    "have",
    "been",
    "will",
    "would",
    "could",
    "should",
    "might",
    "become",
    "into",
    "their",
    "there",
    "which",
    "about",
    "after",
    "before",
    "other",
    "than",
    "then",
    "they",
    "when",
    "where",
]);
function contentWords(text) {
    return [
        ...new Set(text
            .toLowerCase()
            .split(/[^a-z0-9£$%]+/)
            .filter((word) => word.length >= 4 && !STOP_WORDS.has(word))),
    ];
}
/** True when `sentence` restates most of `proposition`'s content words. */
function restates(sentence, proposition) {
    const words = contentWords(proposition);
    if (words.length < 2)
        return false;
    const lower = sentence.toLowerCase();
    const hits = words.filter((word) => lower.includes(word)).length;
    return hits >= Math.max(2, Math.ceil(words.length * 0.6));
}
/**
 * Anti-flattening: a sentence that restates a scenario's consequence must
 * keep the condition; one that restates a derived inference must keep an
 * inferential frame. Applies to every such entry in the corpus, cited or
 * not, so an uncited restatement cannot launder the inference into fact.
 */
function containmentViolation(sentence, entries) {
    for (const entry of entries) {
        if (entry.class === "scenario" && restates(sentence, entry.proposition)) {
            if (!CONDITIONAL_FRAME.test(sentence)) {
                return "states a scenario consequence without its condition";
            }
        }
        if (entry.class === "derived-inference" && restates(sentence, entry.proposition)) {
            const frame = entry.inferenceMode === "hypothesis" ? HYPOTHESIS_FRAME : INFERENCE_FRAME;
            if (!frame.test(sentence) && !CONDITIONAL_FRAME.test(sentence)) {
                return entry.inferenceMode === "hypothesis"
                    ? "restates a hypothesis without keeping it a possibility"
                    : "states a derived inference as established fact";
            }
        }
    }
    return null;
}
/**
 * Anti-laundering across turns (P2). A sentence that restates an inference
 * Insights itself made earlier in the conversation must keep an inferential
 * frame (a hypothesis must stay a possibility; a scenario keeps its
 * condition) unless a cited record independently establishes it
 * (`eligible`) or the sentence explicitly reports what a record states.
 */
export function transientInferenceViolation(sentence, inferences, opts) {
    if (opts.eligible)
        return null;
    for (const inference of inferences) {
        if (!restates(sentence, inference.text))
            continue;
        if (inference.mode === "scenario") {
            if (!CONDITIONAL_FRAME.test(sentence)) {
                return "restates an earlier scenario from this conversation without its condition";
            }
            continue;
        }
        const frame = inference.mode === "hypothesis" ? HYPOTHESIS_FRAME : INFERENCE_FRAME;
        if (frame.test(sentence) || CONDITIONAL_FRAME.test(sentence))
            continue;
        if (inference.mode !== "hypothesis" && REPORTING_FRAME.test(sentence))
            continue;
        return inference.mode === "hypothesis"
            ? "restates an earlier hypothesis from this conversation as established"
            : "restates an earlier inference from this conversation as established fact";
    }
    return null;
}
/** True when the text reports what a record / source states. */
export function hasReportingFrame(text) {
    return REPORTING_FRAME.test(text);
}
/** True when the text uses an inferential or conditional frame. */
export function hasInferenceFrame(text) {
    return INFERENCE_FRAME.test(text) || CONDITIONAL_FRAME.test(text);
}
function contradictedAmong(entries) {
    return entries.some((row) => row.class === "rejected-or-contradicted" ||
        row.evidence.some((ev) => ev.relation === "contradicts"));
}
function falsityViolation(text, cited) {
    if (!FALSITY.test(text) || contradictedAmong(cited))
        return null;
    return "says something is false without a record that contradicts it (an absence of evidence is not evidence of absence)";
}
const CLASS_FRAMES = {
    "recorded-decision": /\b(?:record(?:s|ed)?|decision)\b/i,
    "source-reported": REPORTING_FRAME,
    "unclassified-legacy": /\b(?:record(?:s|ed)?|states?|stated|according to|reports?|source|draft|unclassified|not (?:yet )?(?:classified|adopted|agreed|verified))\b/i,
    "system-record": /\b(?:record(?:s|ed)?|system|shows?)\b/i,
    "recorded-unverified": /\b(?:record(?:s|ed)?|unverified|not (?:yet )?(?:verified|adopted))\b/i,
    "derived-inference": INFERENCE_FRAME,
    proposal: /\bpropos/i,
    assumption: /\bassum/i,
    scenario: CONDITIONAL_FRAME,
    "open-question": /\b(?:unresolved|open|question|undecided|not (?:yet )?(?:decided|resolved|settled))\b/i,
    "rejected-or-contradicted": /\b(?:reject|contradict|disput|not current)/i,
};
function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function stripNegatedSettlement(text) {
    return text.replace(NEGATED_SETTLEMENT, " ");
}
function reportedActorViolation(text, entries, directActors, inferenceFramed = false) {
    const verbs = inferenceFramed ? COMPLETED_ATTRIBUTION_VERBS : ATTRIBUTION_VERBS;
    for (const entry of entries) {
        const actor = entry.attribution.actorLabel;
        if (!actor || entry.attribution.status !== "reported")
            continue;
        if (directActors.has(actor.toLowerCase()))
            continue;
        const pattern = new RegExp(`\\b${escapeRegExp(actor)}\\b\\s+${verbs}\\b`, "i");
        if (pattern.test(text) && !REPORTING_FRAME.test(text)) {
            return `states ${actor}'s position directly, but the record only reports it`;
        }
    }
    return null;
}
function directActorLabels(entries) {
    return new Set(entries
        .filter((row) => row.attribution.status === "direct" && row.attribution.actorLabel)
        .map((row) => (row.attribution.actorLabel ?? "").toLowerCase()));
}
/**
 * Sentence-level guard for Current Position. `cited` are the entries for the
 * sentence's citation ids; citations with no entry (evidence excerpts, body,
 * identity) count as unclassified context.
 */
export function checkPositionSentence(text, citationIds, selection) {
    const cited = citationIds
        .map((id) => selection.byCitation.get(id))
        .filter((row) => Boolean(row));
    const classes = new Set(cited.map((row) => row.class));
    if (cited.length < citationIds.length)
        classes.add("unclassified-legacy");
    const eligible = [...classes].some((cls) => CURRENT_POSITION_ELIGIBLE_CLASSES.has(cls));
    const affirmative = stripNegatedSettlement(text);
    if (SETTLEMENT.test(affirmative) && !classes.has("agreed")) {
        return { ok: false, reason: "asserts settlement or agreement without an agreed claim" };
    }
    if (CONFIRMATION.test(affirmative) && !eligible) {
        return { ok: false, reason: "asserts confirmation without an eligible claim" };
    }
    if (POSITION_LANGUAGE.test(affirmative) && !eligible) {
        return { ok: false, reason: "asserts a position without an eligible claim" };
    }
    const reported = reportedActorViolation(text, cited, directActorLabels(selection.entries));
    if (reported)
        return { ok: false, reason: reported };
    const contained = containmentViolation(text, selection.entries);
    if (contained)
        return { ok: false, reason: contained };
    const falsity = falsityViolation(text, cited);
    if (falsity)
        return { ok: false, reason: falsity };
    if (!eligible) {
        if (GENERIC_DIRECT_ATTRIBUTION.test(text) && !REPORTING_FRAME.test(text)) {
            return { ok: false, reason: "attributes a statement directly without a direct record" };
        }
        const framed = [...classes].some((cls) => CLASS_FRAMES[cls]?.test(text) ?? false);
        if (!framed) {
            return {
                ok: false,
                reason: "states non-position material without its epistemic framing",
            };
        }
    }
    return { ok: true };
}
/**
 * Answer-level guard for Ask (answers carry one citation list, not
 * per-sentence citations). Settlement needs an agreed claim among the cited
 * records; a reported statement cannot be restated as the party's own; a
 * position needs an eligible cited claim.
 */
export function checkAnswerText(text, citationIds, selection, opts = {}) {
    const cited = citationIds
        .map((id) => selection.byCitation.get(id))
        .filter((row) => Boolean(row));
    const classes = new Set(cited.map((row) => row.class));
    const eligible = cited.some((row) => CURRENT_POSITION_ELIGIBLE_CLASSES.has(row.class));
    const affirmative = stripNegatedSettlement(text);
    if (SETTLEMENT.test(affirmative) && !classes.has("agreed")) {
        return { ok: false, reason: "asserts settlement or agreement without an agreed claim" };
    }
    if (POSITION_LANGUAGE.test(affirmative) && !eligible) {
        return { ok: false, reason: "asserts a position without an eligible claim" };
    }
    const directActors = directActorLabels(selection.entries);
    const sentences = text.split(/(?<=[.!?])\s+/);
    for (const sentence of sentences) {
        const reported = reportedActorViolation(sentence, selection.entries, directActors, opts.inferenceFramed === true);
        if (reported)
            return { ok: false, reason: reported };
        const contained = containmentViolation(sentence, selection.entries);
        if (contained)
            return { ok: false, reason: contained };
        const falsity = falsityViolation(sentence, cited);
        if (falsity)
            return { ok: false, reason: falsity };
        if (!eligible && GENERIC_DIRECT_ATTRIBUTION.test(sentence) && !REPORTING_FRAME.test(sentence)) {
            return { ok: false, reason: "attributes a statement directly without a direct record" };
        }
    }
    return { ok: true };
}
export const EPISTEMIC_DISCIPLINE = [
    "EPISTEMIC DISCIPLINE (Claim + Evidence + Stance):",
    "Every record below carries a deterministic class. Storage of a record in Insights does NOT mean it is verified, adopted by the organisation, asserted by a counterparty, agreed between parties, or the current position.",
    "Only ORGANISATION POSITION, AGREED, VERIFIED OBSERVATIONS and COUNTERPARTY POSITIONS may be stated as present position, each exactly as strong as its class.",
    "SOURCE-REPORTED material must be written as what the source states (e.g. “The draft states that …”). Never restate it as the named party's own statement, indication, wish or commitment.",
    "Never turn a draft into agreement, a reported statement into a direct counterparty assertion, a proposal into a position, an assumption into fact, a scenario into a forecast, or an open question into a settled matter.",
    "Do not say or imply that anything is settled, agreed, confirmed, finalised or resolved unless an AGREED record supports it. Do not add risk conclusions, confidence or settlement that the records do not state.",
    "A derived record is never stronger than the records it was derived from. DERIVED INFERENCES are reasoning, not evidence: restate them only with their inferential frame; never as established fact, never as the organisation's position.",
    "SCENARIOS are conditional: every consequence you mention must keep its condition (“If X, Y may follow”). Never flatten a scenario into a forecast.",
    "Keep reasoning modes visibly distinct: retrieval (“The records establish / the draft states …”), synthesis (“Taken together, A, B and C suggest …”), hypothesis (“One possibility worth investigating is …”), scenario (“If X occurs, Y may follow”), implication (“If A holds, it would follow that …”), and organisational knowledge (“The organisation's adopted position is …” — only from ORGANISATION POSITION or AGREED records). Useful new inferences are welcome; label them as inference.",
    "Distinguish a gap from a contradiction: when the records are silent on X, say X is not established by the records — never that X is false. Say something is false or not the case only when a record contradicts it.",
    "UNCLASSIFIED LEGACY records have provenance but no recorded epistemic status: describe them only as recorded values with their source, never as a position, agreement or verified fact.",
    "Notes are explanatory context written by staff; honour any caveat they state.",
].join(" ");
// ─── Published projection (Organisation B allowlist) ────────────────────────
/**
 * The self-contained, allowlisted epistemic projection a publisher may attach
 * to a published fact. No internal ids, emails or staff notes: only what the
 * portal runtime needs to preserve the boundary without querying Core.
 */
export const PublishedEpistemicSchema = z.object({
    state: ClassificationStateSchema,
    class: EpistemicClassSchema,
    claimType: ClaimTypeSchema.nullable(),
    proposition: z.string().max(2_000).nullable(),
    attribution: z.object({
        actorLabel: z.string().max(200).nullable(),
        actorKind: ActorKindSchema.nullable(),
        status: AttributionStatusSchema,
    }),
    verification: VerificationSchema.nullable(),
    selfStance: StanceSchema.nullable(),
    externalStances: z
        .array(z.object({
        actorLabel: z.string().max(200),
        stance: StanceSchema,
        evidenced: z.boolean().default(false),
    }))
        .max(10),
    evidence: z
        .array(z.object({
        relation: EvidenceRelationSchema,
        authority: EvidenceAuthoritySchema,
        source: z.string().max(400),
        asOf: z.string().max(40),
        sourceActorLabel: z.string().max(200).nullable(),
        sourceActorKind: ActorKindSchema.nullable(),
    }))
        .max(10),
    derived: z.boolean(),
    origin: ClaimOriginSchema.nullable().default(null),
    inferenceMode: InferenceModeSchema.nullable().default(null),
    condition: z.string().max(1_000).nullable().default(null),
    conditional: z.boolean().default(false),
    /** Lineage size only — lineage ids are Core-internal and never published. */
    lineageCount: z.number().int().nonnegative().default(0),
});
/** Publisher-side: project a Core entry to the allowlisted portal shape. */
export function publishedEpistemicFromEntry(entry) {
    return PublishedEpistemicSchema.parse({
        state: entry.state,
        class: entry.class,
        claimType: entry.claimType,
        proposition: entry.state === "explicit" ? clip(entry.proposition, 2_000) : null,
        attribution: entry.attribution,
        verification: entry.verification,
        selfStance: entry.selfStance,
        externalStances: entry.externalStances.slice(0, 10),
        evidence: entry.evidence.slice(0, 10).map((row) => ({ ...row, source: clip(row.source, 400) })),
        derived: entry.derived,
        origin: entry.origin,
        inferenceMode: entry.inferenceMode,
        condition: entry.condition ? clip(entry.condition, 1_000) : null,
        conditional: entry.conditional,
        lineageCount: entry.lineage.length,
    });
}
function strongestPublishedAuthority(projection) {
    const order = [
        "primary",
        "controlled-current",
        "secondary",
        "draft",
        "conversational",
        "derived",
    ];
    const present = new Set(projection.evidence.filter((row) => row.relation !== "contradicts").map((row) => row.authority));
    return order.find((authority) => present.has(authority)) ?? null;
}
/**
 * Portal-side selection from the published projection ONLY. A fact without a
 * published projection is classified exactly like staff legacy material.
 */
export function selectPublishedEpistemicEntries(facts, subjectLabel) {
    const entries = facts.map((fact) => {
        const originLabel = fact.originLabel !== subjectLabel ? fact.originLabel : null;
        const projection = fact.epistemic ?? null;
        if (!projection) {
            return entryFromLegacyFact({ ...fact, note: null, originLabel });
        }
        return {
            class: projection.class,
            state: projection.state,
            citationIds: [fact.citationId],
            proposition: projection.proposition ?? factProposition({ ...fact, note: null }),
            claimType: projection.claimType,
            factKey: fact.key,
            source: fact.source,
            asOf: fact.asOf,
            note: null,
            originLabel,
            authority: strongestPublishedAuthority(projection),
            attribution: projection.attribution,
            verification: projection.verification,
            selfStance: projection.selfStance,
            externalStances: projection.externalStances,
            evidence: projection.evidence,
            derived: projection.derived,
            origin: projection.origin,
            inferenceMode: projection.inferenceMode,
            condition: projection.condition,
            conditional: projection.conditional,
            lineage: [],
            competing: false,
            at: fact.asOf,
        };
    });
    const byCitation = new Map();
    for (const entry of entries)
        for (const id of entry.citationIds)
            byCitation.set(id, entry);
    return {
        entries,
        eligible: entries.filter((entry) => CURRENT_POSITION_ELIGIBLE_CLASSES.has(entry.class)),
        byCitation,
    };
}
//# sourceMappingURL=epistemic-selection.js.map